import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { getActivity } from "@/data/activities";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingForm from "@/components/BookingForm";
import { ACTIVITIES } from "@/constants/testIds";
import { ArrowLeft, Clock, Mountain, Users, Tag, Check } from "lucide-react";

export default function ActivityDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const activity = getActivity(slug);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!activity) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-display font-black text-5xl">404</h1>
          <p className="mt-3 text-[#5C635A]">
            {lang === "es" ? "Actividad no encontrada." : "Activity not found."}
          </p>
          <Link to="/" className="mt-6 underline text-[#F26419]">
            {t("act.back")}
          </Link>
        </div>
      </>
    );
  }

  const info = [
    { icon: Clock, label: t("act.duration"), value: activity.duration[lang] },
    { icon: Mountain, label: t("act.difficulty"), value: activity.difficulty[lang] },
    { icon: Users, label: t("act.group"), value: activity.group[lang] },
    { icon: Tag, label: t("act.price"), value: lang === "es" ? activity.price : activity.priceEn },
  ];

  return (
    <>
      <Navbar />
      <section className="relative h-[60vh] min-h-[420px] w-full overflow-hidden">
        <img
          src={activity.image}
          alt={activity.title[lang]}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-12 text-white">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-bold text-white/80 hover:text-[#F26419] mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("act.back")}
          </button>
          <h1 className="font-display font-black text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tighter max-w-4xl">
            {activity.title[lang]}
          </h1>
          <p className="mt-4 text-lg md:text-xl text-white/90 max-w-2xl">{activity.tagline[lang]}</p>
        </div>
      </section>

      <section className="bg-[#F9F8F6] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
              {info.map((i, idx) => (
                <div key={idx} className="bg-white border-2 border-[#1A1B1A] p-5">
                  <i.icon className="w-5 h-5 text-[#F26419] mb-3" />
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#5C635A]">
                    {i.label}
                  </p>
                  <p className="font-display font-bold text-base text-[#1A1B1A] mt-1 leading-tight">
                    {i.value}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-lg leading-relaxed text-[#1A1B1A]">{activity.description[lang]}</p>

            <h3 className="font-display font-black text-2xl md:text-3xl mt-12 mb-6 text-[#1A1B1A]">
              {t("act.includes")}
            </h3>
            <ul className="space-y-3">
              {activity.includes[lang].map((it, i) => (
                <li key={i} className="flex items-start gap-3 text-[#1A1B1A]">
                  <span className="w-6 h-6 bg-[#2D5A3C] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                  </span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>

            {activity.gallery?.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-12">
                {activity.gallery.map((g, i) => (
                  <img
                    key={i}
                    src={g}
                    alt={`${activity.title[lang]} ${i + 1}`}
                    className="w-full aspect-[4/3] object-cover border-2 border-[#1A1B1A]"
                  />
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="sticky top-28">
              <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#F26419] mb-3">
                {t("booking.kicker")}
              </p>
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#1A1B1A] mb-6">
                {t("act.book")}
              </h2>
              <div data-testid={ACTIVITIES.detailBookBtn}>
                <BookingForm defaultActivity={activity.slug} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
